/**
 * The outcome of asking the host app for the viewer's replay read token.
 *
 * The replay read API validates an ENTRA token only, so "no token" is not one
 * state but three, and #30126 was the cost of collapsing them: a signed-in
 * Auth0 viewer was told they were "not signed in", which reads as a broken
 * login and offers no action that helps (signing in again the same way changes
 * nothing). The token factory is the one place that knows which case it is, so
 * it now says so instead of returning a bare `undefined` and leaving each read
 * surface to guess.
 */
export type RynReplayNoTokenReason =
  /** Nobody is signed in. */
  | 'no-session'
  /**
   * A valid session, but not the Entra issuer the read API requires (e.g. an
   * Auth0 session in Software Security). The viewer is signed in; their issuer
   * simply cannot read replay.
   */
  | 'wrong-issuer'
  /** The right (Entra) issuer, but the silent token acquisition failed. */
  | 'acquire-failed';

/**
 * A viewer access token, or the reason one could not be produced. `token` is
 * always a non-empty string when present; every no-token outcome carries a
 * `reason` so the read surfaces can word the refusal for the actual state
 * rather than asserting a single "not signed in" for all three.
 */
export type RynReplayAccessToken =
  | { readonly token: string; readonly reason?: undefined }
  | { readonly token?: undefined; readonly reason: RynReplayNoTokenReason };
